import { FC, useMemo, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Switch,
  Typography,
} from "@mui/material"
import { Add, DeleteOutlined, Edit, PlayArrow } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { useSnackbar } from "notistack"
import { TooltipAction } from "../common/tooltipAction"
import { DataGrid, Modal } from "@/core/components"
import { useUrlListState } from "@/core/hooks"
import { Userbot } from "../../interfaces"
import {
  useDeleteScheduledChainMutation,
  useRolesQuery,
  useRunScheduledNowMutation,
  useScheduledChainsQuery,
  useUpdateScheduledChainMutation,
} from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { IdentityResolverCell } from "../identity"
import { ScheduledChainForm } from "./scheduledChainForm"
import { getApiErrorMessage } from "@/core/utils"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import { launchResultMessage, launchResultPath } from "../common/launchResult"

const ScheduledTargetCell: FC<{
  roleId: string | null | undefined
  clientId: string | null | undefined
  roleNameMap: Map<string, string>
}> = ({ roleId, clientId, roleNameMap }) => {
  if (roleId) {
    const roleName = roleNameMap.get(roleId) ?? roleId
    return <Chip size="small" label={`Роль: ${roleName}`} />
  }
  if (clientId) {
    return <IdentityResolverCell clientId={clientId} />
  }
  return (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  )
}

export const ScheduledChainsTable: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const list = useUrlListState()
  const { data, isFetching, isError, error } = useScheduledChainsQuery(
    list.listArgs,
    { pollingInterval: 60000 },
  )
  const { data: rolesData } = useRolesQuery({ limit: 500 })
  const canWrite = useUserbotWriteAccess()
  const [deleteScheduled, { isLoading: isDeleting }] =
    useDeleteScheduledChainMutation()
  const [updateScheduled] = useUpdateScheduledChainMutation()
  const [runNow, { isLoading: isRunning }] = useRunScheduledNowMutation()

  const [editing, setEditing] = useState<Userbot.ScheduledChain | null>(null)
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<Userbot.ScheduledChain | null>(null)
  const [toEnable, setToEnable] = useState<Userbot.ScheduledChain | null>(null)
  const inFlight = useRef(false)
  const [actionLoading, setActionLoading] = useState(false)

  const roleNameMap = useMemo(() => {
    const map = new Map<string, string>()
    rolesData?.items?.forEach((r) => map.set(r.id, r.name))
    return map
  }, [rolesData])

  const handleRunNow = async (row: Userbot.ScheduledChain) => {
    if (!canWrite || inFlight.current) return
    inFlight.current = true
    setActionLoading(true)
    try {
      const result = await runNow({ id: row.id }).unwrap()
      enqueueSnackbar(launchResultMessage(result), { variant: "success" })
      const path = launchResultPath(result)
      if (path) navigate(path)
    } catch (e) {
      const status = (e as { status?: number | string })?.status
      if (status === 409) {
        enqueueSnackbar(
          "Конфликт: предыдущий запуск ещё не завершён. История сохранена.",
          {
            variant: "warning",
          },
        )
      } else {
        enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
      }
    } finally {
      inFlight.current = false
      setActionLoading(false)
    }
  }

  const columns: GridColDef<Userbot.ScheduledChain>[] = [
    {
      field: "name",
      headerName: "Название",
      flex: 2,
    },
    {
      field: "runs",
      headerName: "История",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          component={RouterLink}
          to={`/userbot/scheduled/${row.id}/runs`}
          aria-label={`История запусков ${row.id}`}
        >
          Запуски
        </Link>
      ),
    },
    {
      field: "target",
      headerName: "Исполнитель",
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => (
        <ScheduledTargetCell
          roleId={row.role}
          clientId={row.clientId}
          roleNameMap={roleNameMap}
        />
      ),
    },
    { field: "cron", headerName: "Cron", flex: 2, sortable: false },
    {
      field: "tz",
      headerName: "Часовой пояс",
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => row.tz ?? "UTC",
    },
    {
      field: "overlap",
      headerName: "Overlap",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={row.overlap === "allow" ? "success" : "default"}
          label={row.overlap}
        />
      ),
    },
    {
      field: "lastRunAt",
      headerName: "Последний запуск",
      flex: 2,
      valueGetter: ({ value }) =>
        value ? new Date(value).toLocaleString("ru") : "—",
    },
    {
      field: "isActive",
      headerName: "Активно",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Switch
          checked={row.isActive}
          disabled={!canWrite || actionLoading}
          onChange={(e) =>
            e.target.checked
              ? setToEnable(row)
              : updateScheduled({
                  id: row.id,
                  body: { isActive: e.target.checked },
                })
          }
        />
      ),
    },
  ]

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button startIcon={<Add />} onClick={() => setCreating(true)}>
            Создать расписание
          </Button>
        </Box>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}
      <Box height="80vh">
        <DataGrid<Userbot.ScheduledChain>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={
            canWrite
              ? [
                  (r) => (
                    <TooltipAction
                      label="Запустить сейчас"
                      icon={<PlayArrow />}
                      onClick={() => handleRunNow(r.row)}
                      disabled={isRunning || actionLoading}
                    />
                  ),
                  (r) => (
                    <TooltipAction
                      label="Редактировать"
                      icon={<Edit />}
                      onClick={() => setEditing(r.row)}
                    />
                  ),
                  (r) => (
                    <TooltipAction
                      label="Удалить"
                      icon={<DeleteOutlined />}
                      onClick={() => setToDelete(r.row)}
                    />
                  ),
                ]
              : undefined
          }
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новое расписание"
          onClose={() => setCreating(false)}
          dialogProps={{ maxWidth: "lg" }}
          element={<ScheduledChainForm onDone={() => setCreating(false)} />}
        />
      )}
      {editing && (
        <Modal
          disableBackdropClose
          title={`Расписание: ${editing.name}`}
          onClose={() => setEditing(null)}
          dialogProps={{ maxWidth: "lg" }}
          element={
            <ScheduledChainForm
              scheduled={editing}
              onDone={() => setEditing(null)}
            />
          }
        />
      )}
      <ConfirmDialog
        open={!!toEnable}
        title="Включить расписание?"
        loading={actionLoading}
        description={
          toEnable
            ? `Cron начнёт регулярные запуски: ${toEnable.name}`
            : undefined
        }
        onCancel={() => {
          if (inFlight.current) return
          setToEnable(null)
        }}
        onConfirm={async () => {
          const row = toEnable
          if (!row || !canWrite || inFlight.current) return
          inFlight.current = true
          setActionLoading(true)
          try {
            await updateScheduled({
              id: row.id,
              body: { isActive: true },
            }).unwrap()
            setToEnable(null)
          } catch (e) {
            enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
          } finally {
            inFlight.current = false
            setActionLoading(false)
          }
        }}
      />
      <ConfirmDialog
        open={!!toDelete}
        title="Удалить расписание?"
        description={toDelete?.name}
        loading={isDeleting}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteScheduled({ id: toDelete.id })
            setToDelete(null)
          }
        }}
      />
    </>
  )
}
